import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RunRecord } from '../types';
import { getReview } from '../services/api';

interface Props {
  run: RunRecord;
}

interface ReviewData {
  runId: string;
  status: string;
  title?: string;
  content?: string;
  citationStyle?: string;
  sections?: { id: string; title: string; excerpt?: string }[];
}

interface ParsedContent {
  bodyHtml: string;
  referencesHtml: string;
  refCount: number;
}

interface ReferenceItem {
  index: number;
  html: string;
  href: string;
  doi: string;
  url: string;
}

const reviewCache = new Map<string, ReviewData>();
const reviewInflight = new Map<string, Promise<ReviewData>>();

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function slugifyHeading(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-').replace(/^-|-$/g, '');
}

function extractDoi(line: string): string {
  const match = line.match(/DOI:\s*(10\.\S+)/i);
  return match ? match[1].replace(/[.,;:]*$/, '') : '';
}

function extractUrl(line: string): string {
  const match = line.match(/URL:\s*(https?:\/\/\S+)/i);
  return match ? match[1].replace(/[.,;]*$/, '') : '';
}

function renderInline(text: string): string {
  const escaped = escapeHtml(text);
  return escaped
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
}

function expandCitationToken(token: string): number[] {
  if (/^\d+$/.test(token)) return [parseInt(token, 10)];
  const rangeMatch = token.match(/^(\d+)\s*-\s*(\d+)$/);
  if (!rangeMatch) return [];
  const start = parseInt(rangeMatch[1], 10);
  const end = parseInt(rangeMatch[2], 10);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return [];
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

function renderCitationGroup(raw: string, referenceMap: Map<number, ReferenceItem>): string {
  const tokens = raw.split(',').map((token) => token.trim()).filter(Boolean);
  const linked = tokens
    .map((token) => {
      const refs = expandCitationToken(token);
      if (refs.length === 0) return token;
      const anchors = refs.map((num) => {
        const item = referenceMap.get(num);
        const href = item?.href || `#ref-${num}`;
        const externalAttrs = item?.href ? ' target="_blank" rel="noreferrer"' : '';
        return `<a href="${escapeHtml(href)}" class="citation-link"${externalAttrs}>[${num}]</a>`;
      });
      return anchors.join('');
    })
    .join(', ');

  return `<sup class="citation-group">${linked}</sup>`;
}

function renderInlineWithCitations(text: string, referenceMap: Map<number, ReferenceItem>): string {
  return renderInline(text).replace(/\[(\d+(?:\s*,\s*\d+|\s*-\s*\d+)*)\]/g, (_match, refs) =>
    renderCitationGroup(refs, referenceMap),
  );
}

function splitReviewMarkdown(content: string): { bodyText: string; refsText: string } {
  const lines = content.split('\n');
  const headingIndex = lines.findIndex((line) => /^(?:#{1,3}\s*)?(参考文献|References)\s*$/i.test(line.trim()));
  if (headingIndex >= 0) {
    return {
      bodyText: lines.slice(0, headingIndex).join('\n').trim(),
      refsText: lines.slice(headingIndex + 1).join('\n').trim(),
    };
  }

  const refStart = lines.findIndex((line, index) => /^\[\d+\]\s+/.test(line.trim()) && index > lines.length * 0.5);
  if (refStart >= 0) {
    return {
      bodyText: lines.slice(0, refStart).join('\n').trim(),
      refsText: lines.slice(refStart).join('\n').trim(),
    };
  }

  return { bodyText: content, refsText: '' };
}

function parseReferenceItems(refsText: string): ReferenceItem[] {
  const refLines = refsText.split('\n').map((line) => line.trim()).filter(Boolean);
  const items: ReferenceItem[] = [];

  for (const line of refLines) {
    const match = line.match(/^\[(\d+)\]\s+(.+)/);
    if (!match) continue;

    const index = parseInt(match[1], 10);
    const text = match[2];
    const doi = extractDoi(text);
    const url = extractUrl(text);
    const href = url || (doi ? `https://doi.org/${doi}` : '');
    let displayText = text
      .replace(/URL:\s*https?:\/\/\S+/i, '')
      .replace(/DOI:\s*10\.\S+/i, '')
      .trim()
      .replace(/[.,;:\s]+$/, '');

    let html = renderInline(displayText);
    if (doi) {
      html += ` <a href="https://doi.org/${escapeHtml(doi)}" class="ref-doi-link" target="_blank" rel="noreferrer">DOI</a>`;
    }
    if (url) {
      html += ` <a href="${escapeHtml(url)}" class="ref-doi-link" target="_blank" rel="noreferrer">URL</a>`;
    }
    items.push({ index, html, href, doi, url });
  }

  return items.sort((a, b) => a.index - b.index);
}

function renderMarkdownBlock(markdown: string, referenceMap: Map<number, ReferenceItem>): string {
  const htmlLines: string[] = [];
  let inParagraph = false;
  let listType: 'ul' | 'ol' | null = null;

  function closeParagraph() {
    if (inParagraph) {
      htmlLines.push('</p>');
      inParagraph = false;
    }
  }

  function closeList() {
    if (listType) {
      htmlLines.push(`</${listType}>`);
      listType = null;
    }
  }

  function openList(type: 'ul' | 'ol') {
    if (listType !== type) {
      closeList();
      htmlLines.push(`<${type}>`);
      listType = type;
    }
  }

  for (const rawLine of markdown.split('\n')) {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();
    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);

    if (headingMatch) {
      closeParagraph();
      closeList();
      const level = headingMatch[1].length;
      const text = headingMatch[2].trim();
      htmlLines.push(`<h${level} id="${slugifyHeading(text)}">${renderInlineWithCitations(text, referenceMap)}</h${level}>`);
      continue;
    }

    if (/^---+\s*$/.test(trimmed) || /^\*\*\*+\s*$/.test(trimmed)) {
      closeParagraph();
      closeList();
      htmlLines.push('<hr />');
      continue;
    }

    if (trimmed === '') {
      closeParagraph();
      closeList();
      continue;
    }

    if (/^\s*[-*+]\s+/.test(line)) {
      closeParagraph();
      openList('ul');
      htmlLines.push(`<li>${renderInlineWithCitations(line.replace(/^\s*[-*+]\s+/, ''), referenceMap)}</li>`);
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      closeParagraph();
      openList('ol');
      htmlLines.push(`<li>${renderInlineWithCitations(line.replace(/^\s*\d+\.\s+/, ''), referenceMap)}</li>`);
      continue;
    }

    closeList();
    if (!inParagraph) {
      htmlLines.push('<p>');
      inParagraph = true;
    } else {
      htmlLines.push('<br />');
    }
    htmlLines.push(renderInlineWithCitations(trimmed, referenceMap));
  }

  closeParagraph();
  closeList();
  return htmlLines.join('\n');
}

function parseReviewContent(content: string): ParsedContent {
  const { bodyText, refsText } = splitReviewMarkdown(content);
  const refItems = parseReferenceItems(refsText);
  const referenceMap = new Map(refItems.map((item) => [item.index, item]));
  const bodyHtml = renderMarkdownBlock(bodyText, referenceMap);
  const referencesHtml = refItems.length > 0
    ? `<ol class="reference-list">${refItems
        .map((item) => `<li id="ref-${item.index}"><span class="ref-text">${item.html}</span></li>`)
        .join('')}</ol>`
    : '';

  return {
    bodyHtml,
    referencesHtml,
    refCount: refItems.length,
  };
}

function getDownloadGlyph(label: string): string {
  if (label.endsWith('.bib')) return 'B';
  if (label.endsWith('.md')) return 'M';
  if (label.endsWith('.json')) return 'J';
  return 'F';
}

export default function ResultsPanel({ run }: Props) {
  const { t } = useTranslation();
  const [review, setReview] = useState<ReviewData | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');

  useEffect(() => {
    let cancelled = false;

    if (run.status !== 'completed') {
      setReview(null);
      setReviewError('');
      setReviewLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const cached = reviewCache.get(run.runId);
    if (cached) {
      setReview(cached);
      setReviewError('');
      setReviewLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const inflight = reviewInflight.get(run.runId);
    if (inflight) {
      setReviewLoading(true);
      setReviewError('');
      inflight
        .then((data) => {
          if (!cancelled) setReview(data);
        })
        .catch((err: Error) => {
          if (!cancelled) setReviewError(err.message || 'Failed');
        })
        .finally(() => {
          if (!cancelled) setReviewLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    setReviewLoading(true);
    setReviewError('');

    const request = getReview(run.runId).then((data) => data as unknown as ReviewData);
    reviewInflight.set(run.runId, request);

    request
      .then((data) => {
        reviewCache.set(run.runId, data);
        if (!cancelled) setReview(data);
      })
      .catch((err: Error) => {
        if (!cancelled) setReviewError(err.message || 'Failed');
      })
      .finally(() => {
        reviewInflight.delete(run.runId);
        if (!cancelled) setReviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [run.runId, run.status]);

  const parsed = useMemo(() => {
    if (!review?.content) return null;
    try {
      return parseReviewContent(review.content);
    } catch {
      return null;
    }
  }, [review?.content]);

  if (!['completed', 'failed', 'interrupted'].includes(run.status) && !reviewLoading) return null;

  const downloads = run.downloads.filter((download) => download.status === 'ready');
  const hasReviewArtifact = downloads.some((download) => download.label === 'review.md');
  const hasContent = Boolean(parsed && !reviewLoading);

  return (
    <section className="results-panel review-reader-shell">
      <div className="review-split">
        <article className="review-main">
          {reviewLoading && (
            <div className="review-loading">
              <div className="skeleton" style={{ height: '1.2rem', width: '68%', marginBottom: '1rem' }} />
              <div className="skeleton" style={{ height: '0.95rem', width: '92%', marginBottom: '0.65rem' }} />
              <div className="skeleton" style={{ height: '0.95rem', width: '88%', marginBottom: '0.65rem' }} />
              <div className="skeleton" style={{ height: '0.95rem', width: '78%', marginBottom: '1.2rem' }} />
              <div className="skeleton" style={{ height: '0.95rem', width: '95%', marginBottom: '0.65rem' }} />
              <div className="skeleton" style={{ height: '0.95rem', width: '86%' }} />
            </div>
          )}

          {reviewError && !reviewLoading && <div className="review-error">{t('review.failedToLoad')}: {reviewError}</div>}

          {hasContent && parsed && (
            <>
              <header className="review-meta">
                <div className="review-meta-copy">{review?.title && <h1 className="review-title">{review.title}</h1>}</div>
              </header>

              <div className="review-body" dangerouslySetInnerHTML={{ __html: parsed.bodyHtml }} />

              {parsed.refCount > 0 && (
                <section className="review-references">
                  <h3>{t('review.references')}</h3>
                  <div className="reference-list-container" dangerouslySetInnerHTML={{ __html: parsed.referencesHtml }} />
                </section>
              )}
            </>
          )}

          {!reviewLoading && !reviewError && !hasContent && <div className="review-empty">{t('review.noContent')}</div>}
        </article>

        <aside className="review-sidebar">
          <div className="review-sidebar-sticky">
            <section className="sidebar-section">
              <h4 className="sidebar-label">{t('download.download')}</h4>
              <div className="download-stack">
                {downloads.map((download) => (
                  <a key={download.label} href={download.url} className="download-item" download>
                    <span className="download-item-icon">{getDownloadGlyph(download.label)}</span>
                    <span className="download-item-name">{download.label}</span>
                  </a>
                ))}

                {review?.content && !hasReviewArtifact && (
                  <button
                    type="button"
                    className="download-item download-item-btn"
                    onClick={() => {
                      const content = review.content;
                      if (!content) return;
                      const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `${review.title || 'review'}.md`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <span className="download-item-icon">M</span>
                    <span className="download-item-name">{t('review.downloadMarkdown')}</span>
                  </button>
                )}
              </div>
            </section>
          </div>
        </aside>
      </div>
    </section>
  );
}
